/**
 * Generate a structured health summary based on organ health percentages
 * @param {Object} organHealthPercentages - Object with organ keys and health scores
 * @returns {Object} Structured health summary with summary, highlights, concerns, and suggestion
 */
export const generateHealthSummary = (organHealthPercentages) => {
  const organs = Object.entries(organHealthPercentages)
    .filter(([_, score]) => score != null && !isNaN(score))
    .map(([name, score]) => ({ name, score: Number(score) }));

  if (organs.length === 0) {
    return {
      summary: "No health data available. Complete a health assessment to get your personalized organ health summary.",
      highlights: [],
      concerns: [],
      suggestion: "Take the health assessment to unlock your personalized organ health insights."
    };
  }

  // Sort by score
  const sorted = [...organs].sort((a, b) => b.score - a.score);
  const strongest = sorted.slice(0, 2);
  const weakest = sorted.slice(-2).reverse();

  // Calculate average
  const avgScore = Math.round(organs.reduce((acc, o) => acc + o.score, 0) / organs.length);

  // Generate summary text
  let summaryText = "";
  if (avgScore >= 80) {
    summaryText = `Your overall organ health is excellent at ${avgScore}%. Your body systems are functioning optimally with strong maintenance across all key areas.`;
  } else if (avgScore >= 60) {
    summaryText = `Your overall organ health is good at ${avgScore}%. While most systems are stable, there is room for targeted improvements in specific areas.`;
  } else if (avgScore >= 40) {
    summaryText = `Your overall organ health is fair at ${avgScore}%. Some organs need attention. Focusing on lifestyle changes can help improve your scores.`;
  } else {
    summaryText = `Your overall organ health needs attention at ${avgScore}%. Multiple systems require care. Prioritizing healthy habits will help restore balance.`;
  }

  // Format organ names nicely
  const formatOrganName = (name) => name.charAt(0).toUpperCase() + name.slice(1);

  // Generate highlights
  const highlights = strongest.map(o => 
    `${formatOrganName(o.name)} at ${o.score}% - performing well`
  );

  // Generate concerns
  const concerns = weakest
    .filter(o => o.score < 60)
    .map(o => `${formatOrganName(o.name)} at ${o.score}% - needs attention`);

  // Generate suggestion based on weakest organ
  const suggestions = {
    heart: "Add 30 minutes of moderate exercise daily to boost heart health.",
    brain: "Prioritize 7-8 hours of quality sleep for better brain recovery.",
    liver: "Reduce processed foods and alcohol to support liver detox.",
    lung: "Practice deep breathing exercises and avoid smoke exposure.",
    lungs: "Practice deep breathing exercises and avoid smoke exposure.",
    kidney: "Stay well-hydrated by drinking 8 glasses of water daily.",
    stomach: "Eat smaller, frequent meals and include more fiber in your diet."
  };

  const suggestion = weakest.length > 0 
    ? suggestions[weakest[0].name] || "Focus on balanced nutrition and regular exercise for overall health improvement."
    : "Maintain your healthy lifestyle to keep all organs performing optimally.";

  return {
    summary: summaryText,
    highlights,
    concerns: concerns.length > 0 ? concerns : [],
    suggestion
  };
};

export default generateHealthSummary;
